import { HeaderViewProps } from './header.types';
import '@/assets/styles/components/header.styles.scss';
import { useLocation, useNavigate } from 'react-router';
import { NavigationPath, WithLandingHeader } from '@/router/navigation-path.ts';
import { DialogModal, ImageWithFallback } from '@/components';
import { Fragment, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useBoutiqueMenu, useQueryProvider } from '@/hooks';
import { BOUTIQUE_GROUP_NAME, BOUTIQUE_GROUP_TITLE, BoutiqueMenuItem } from '@/hooks/use-boutique-menu';
import { useApi } from '@/api/useApi.tsx';
import { parseJwt, jsonTryParse } from '@/utility';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { useAppSettings } from '@/zustand/app-settings/store.ts';
import clsx from 'clsx';
import { APPS, LOGOUT_DIALOG } from '@/constants';
import CoachLogo from '@/assets/svg/coach-logo-with-text.svg';
import SolverLogo from '@/assets/svg/solver-logo-with-text.svg';
import GotItItLogo from '@/assets/svg/got-it-logo-with-text.svg';
import BoutiqueLogo from '@/assets/svg/boutique-logo-with-text.svg';
import { useNotificationStore } from '@/zustand/notification/notificationStore.ts';
import { CallModal, type CallModalRef, PhoneDialerModal } from '@/components/call-modal';
import { useFeatureFlag } from 'configcat-react';

interface ServiceGroupInfo {
	id: string;
	title: string;
	icon: React.ReactNode;
}

const SERVICE_GROUP_MAPPING: Record<string, ServiceGroupInfo> = {
	COACHING: {
		id: APPS.COACH,
		title: APPS.COACH,
		icon: (
			<img
				src={CoachLogo}
				className="coach-logo"
			/>
		)
	},
	GOT_IT: {
		id: APPS.GOT_IT,
		title: APPS.GOT_IT,
		icon: (
			<img
				src={GotItItLogo}
				className="gotit-logo"
			/>
		)
	},
	SOLVER: {
		id: APPS.SOLVER,
		title: APPS.SOLVER,
		icon: (
			<img
				src={SolverLogo}
				className="solver-logo"
			/>
		)
	},
	[BOUTIQUE_GROUP_NAME]: {
		id: BOUTIQUE_GROUP_NAME,
		title: BOUTIQUE_GROUP_TITLE,
		icon: (
			<img
				src={BoutiqueLogo}
				className="boutique-logo"
			/>
		)
	}
};

const SUB_MENU_MAPPING = {
	[BoutiqueMenuItem.CALENDAR]: {
		icon: 'icons-calendar',
		navigationPath: NavigationPath.boutiqueClassroom
	},
	[BoutiqueMenuItem.MY_STUDENTS]: {
		icon: 'icons-users-group-rounded',
		navigationPath: NavigationPath.boutiqueStudents
	},
	MY_APPOINTMENTS: {
		icon: 'icons-user-card',
		navigationPath: NavigationPath.coachAppointments
	},
	INSTANT_MEETINGS: {
		icon: 'icons-videocamera-record',
		navigationPath: NavigationPath.coachInstantMeetings
	},
	MY_STUDENTS: {
		icon: 'icons-users-group-rounded',
		navigationPath: NavigationPath.coachStudents
	},
	GOT_IT_MY_STUDENTS: {
		icon: 'icons-users-group-rounded',
		navigationPath: NavigationPath.gotItStudents
	},
	PAST_MEETINGS: {
		icon: 'icons-chat-round-video',
		navigationPath: NavigationPath.coachPastMeetings
	},
	GOT_IT_MY_APPOINTMENTS: {
		icon: 'icons-user-card',
		navigationPath: NavigationPath.gotItAppointments
	},
	GOT_IT_INSTANT_MEETINGS: {
		icon: 'icons-videocamera-record',
		navigationPath: NavigationPath.gotItInstantMeetings
	},
	GOT_IT_PAST_MEETINGS: {
		icon: 'icons-chat-round-video',
		navigationPath: NavigationPath.gotItPastMeetings
	},
	GOT_IT_PLANNED_PAST_MEETINGS: {
		icon: 'icons-chat-round-video',
		navigationPath: NavigationPath.gotItPlannedPastMeetings
	},
	GOT_IT_PLANNED_MY_APPOINTMENTS: {
		icon: 'icons-user-card',
		navigationPath: NavigationPath.gotItPlannedAppointments
	},
	EXPERT_REVIEW: {
		icon: 'icons-star',
		navigationPath: NavigationPath.solverExpertReview
	},
	SOLVE_QUESTION: {
		icon: 'icons-magic-stick',
		navigationPath: NavigationPath.solverSolve
	},
	ANSWERED_QUESTIONS: {
		icon: 'icons-clipboard-check',
		navigationPath: NavigationPath.solverSolvedQuestions
	},
	RESOLVE_QUESTIONS: {
		icon: 'icons-undo-left-arrow',
		navigationPath: NavigationPath.solverResolveQuestions
	}
};

const HeaderView = ({ onDynamicRegisterOrSignInButton = () => null, announcements = 0, onMessageClick, onAnnouncementClick, onProfileClick }: HeaderViewProps) => {
	const { ExpertControllerApi, ExpertApplicantControllerApi, MenuItemControllerApi, ChatMetricControllerApi, ExpertNotificationControllerApi, SupportDocumentControllerApi } = useApi();
	const { data: authData, clearData: clearAuth, setData: setAuthData } = useAuthStore();
	const { data: appSettingsData, setData: setAppSettingsData } = useAppSettings();
	const location = useLocation();
	const navigate = useNavigate();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [openGroups, setOpenGroups] = useState<string[]>([]);
	const dropdownRef = useRef(null);
	const profileRef = useRef(null);
	const callModalRef = useRef<CallModalRef>(null);
	const [isPhoneDialerOpen, setIsPhoneDialerOpen] = useState(false);

	const { value: isActivePhoneDialer } = useFeatureFlag('isActivePhoneDialer', false);
	const { value: isActivePhoneDialerExpertIds } = useFeatureFlag('isActivePhoneDialerExpertIds', '[]');
	const { value: reviewerExpertIdListRaw } = useFeatureFlag('reviewerExpertIdList', '[]');
	const { value: assignToMeExpertIdListRaw } = useFeatureFlag('assignToMeExpertIdList', '[]');

	const expertId = useMemo(() => (authData?.access_token ? parseJwt(authData?.access_token)?.additional_info?.expert_id : ''), [authData?.access_token]);

	const isCallFeatureActive = useMemo(
		() => isActivePhoneDialer || jsonTryParse(isActivePhoneDialerExpertIds).some((id: number) => id === parseInt(expertId)),
		[isActivePhoneDialer, isActivePhoneDialerExpertIds, expertId]
	);

	const isReviewer = useMemo(() => jsonTryParse(reviewerExpertIdListRaw as string).some((id: number) => id === parseInt(expertId)), [reviewerExpertIdListRaw, expertId]);

	const isActiveAssignToMe = useMemo(() => jsonTryParse(assignToMeExpertIdListRaw as string).some((id: number) => id === parseInt(expertId)), [assignToMeExpertIdListRaw, expertId]);

	const handlePhoneDialerOpen = useCallback(() => {
		setIsPhoneDialerOpen(true);
		setIsMobileMenuOpen(false);
	}, []);

	const handleDirectCall = useCallback((phoneNumber: string) => {
		callModalRef.current?.handleDirectCall(phoneNumber);
	}, []);

	const isLandingPage = useMemo(() => WithLandingHeader[location.pathname as keyof typeof WithLandingHeader], [location.pathname]);

	const isLoginPage = location.pathname === NavigationPath.login;

	const isApplicant = useMemo(() => {
		if (authData?.access_token) {
			const resourceAccess = parseJwt(authData?.access_token)?.resource_access;
			return resourceAccess?.['live-expert-applicant']?.roles?.includes('Expert_Applicant');
		}
		return false;
	}, [authData?.access_token]);

	const { data: expertDetailInfo } = useQueryProvider([QUERY_KEYS.getLoggedInExpertDetailInfo], () => ExpertControllerApi.getLoggedInExpertDetailInfo(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		onSuccess: response => {
			setAppSettingsData({
				...appSettingsData,
				isOpenBankInformation: !response?.isPaymentInfoCreated
			});
		},
		enabled: !isApplicant && !isLandingPage
	});

	const { data: expertApplicantData } = useQueryProvider([QUERY_KEYS.getLoggedInExpertApplicantDetailInfo], () => ExpertApplicantControllerApi.getLoggedInExpertApplicantDetailInfo(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		enabled: !!isApplicant && !isLandingPage
	});

	const { data: apiResponse } = useQueryProvider([QUERY_KEYS.getAllMenuItems], () => MenuItemControllerApi.getAllMenuItems(), {
		select: ({ data }) => data,
		refetchOnWindowFocus: false,
		enabled: !isLandingPage && !isApplicant
	});

	const { data: supportDocTotal } = useQueryProvider([QUERY_KEYS.findSupportDocuments, 'menu-check'], () => SupportDocumentControllerApi.findSupportDocuments(undefined, undefined, 0, 1), {
		select: ({ data }) => data?.data?.totalElements ?? 0,
		refetchOnWindowFocus: false,
		enabled: !isLandingPage && !isApplicant
	});

	const hasSupportDocs = (supportDocTotal ?? 0) > 0;

	const { data: messages } = useQueryProvider([QUERY_KEYS.findExpertUnreadChatCount], () => ChatMetricControllerApi.findExpertUnreadChatCount(), {
		select: ({ data }) => data?.data?.unreadChatCount || 0,
		refetchOnWindowFocus: 'always',
		enabled: !isLandingPage && !isApplicant
	});

	const { data: unreadNotifications } = useQueryProvider([QUERY_KEYS.getExpertNotificationUnreadCount], () => ExpertNotificationControllerApi.getExpertNotificationUnreadCount(), {
		select: ({ data }) => data.data,
		enabled: !isLandingPage && !isApplicant,
		refetchOnWindowFocus: false
	});

	const unreadNotificationCount = useNotificationStore(s => s.unreadNotifications);
	const updateNotificationCount = useNotificationStore(s => s.setUnreadNotificationCount);

	useEffect(() => {
		if (unreadNotifications) {
			updateNotificationCount(unreadNotifications);
		}
	}, [unreadNotifications]);

	const { hasBoutiqueAccess, boutiqueMenuGroup } = useBoutiqueMenu({ enabled: !isLandingPage && !isApplicant });
	const menuItems = useMemo(() => [...(hasBoutiqueAccess ? [boutiqueMenuGroup] : []), ...(apiResponse?.data ?? [])], [apiResponse, boutiqueMenuGroup, hasBoutiqueAccess]);

	useEffect(() => {
		if (menuItems.length > 0) {
			setOpenGroups(menuItems.map(group => group.liveServiceGroupName));
		}
	}, [menuItems]);

	const toggleGroup = groupId => {
		setOpenGroups(prev => (prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]));
	};

	const user = useMemo(() => (isApplicant ? expertApplicantData : expertDetailInfo), [isApplicant, expertApplicantData, expertDetailInfo]);

	useEffect(() => {
		if (user) {
			const profileData = user?.expert || user?.expertApplicant;
			if (profileData?.profilePicture) {
				setAuthData({
					...authData,
					avatar: profileData?.profilePicture
				});
			}
		}
	}, [user]);

	useEffect(() => {
		const handleClickOutside = event => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target) && profileRef.current && !profileRef.current.contains(event.target)) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (isMobileMenuOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}

		return () => {
			document.body.style.overflow = '';
		};
	}, [isMobileMenuOpen]);

	const handleNavigateHome = useCallback(() => {
		navigate(isLandingPage ? NavigationPath.login : NavigationPath.coachAppointments);
	}, [isLandingPage, navigate]);

	const onNavigateNotifications = () => {
		navigate(NavigationPath.notifications);
	};

	const userProfile = useMemo(() => {
		if (!user) return null;

		const profileData = user?.expert || user?.expertApplicant;
		return {
			name: profileData?.name || '',
			surname: profileData?.surname || '',
			profilePicture: profileData?.profilePicture || '',
			specialization: user?.specialization?.description || ''
		};
	}, [user]);

	const isMobileDeviceAndInProfilePage = useMemo(() => {
		return location.pathname === NavigationPath.profile && window.innerWidth < 820;
	}, [location.pathname, window.innerWidth]);

	const toggleDropdown = () => {
		if (window.innerWidth < 820) {
			return navigate(NavigationPath.profile);
		}

		setIsDropdownOpen(!isDropdownOpen);
	};

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	const handleMobileMenuNavigate = (path: string) => {
		if (path) {
			navigate(path);
			toggleMobileMenu();
		}
	};

	const handleProfileClick = () => {
		setIsDropdownOpen(false);
		navigate(NavigationPath.profile);
	};

	const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

	const handleLogout = () => {
		setIsDropdownOpen(false);
		setIsMobileMenuOpen(false);
		setIsLogoutConfirmOpen(true);
	};

	const handleCancelLogout = () => setIsLogoutConfirmOpen(false);

	const handleConfirmLogout = () => {
		setIsLogoutConfirmOpen(false);
		clearAuth();
		navigate(NavigationPath.login);
	};

	if (isLandingPage) {
		return renderLandingHeader(handleNavigateHome, onDynamicRegisterOrSignInButton, isLoginPage, isMobileMenuOpen, toggleMobileMenu);
	}

	return (
		<>
			{renderAppHeader(
				handleNavigateHome,
				{
					messages,
					unreadNotificationCount,
					announcements,
					onMessageClick,
					onAnnouncementClick,
					onProfileClick,
					userProfile,
					isApplicant,
					handleProfileClick,
					handleLogout
				},
				isDropdownOpen,
				toggleDropdown,
				isMobileDeviceAndInProfilePage,
				dropdownRef,
				profileRef,
				isMobileMenuOpen,
				toggleMobileMenu,
				handleMobileMenuNavigate,
				menuItems,
				openGroups,
				toggleGroup,
				onNavigateNotifications,
				isCallFeatureActive,
				handlePhoneDialerOpen,
				isPhoneDialerOpen,
				setIsPhoneDialerOpen,
				handleDirectCall,
				callModalRef,
				isReviewer,
				isActiveAssignToMe,
				hasSupportDocs,
				navigate
			)}
			<DialogModal
				isOpen={isLogoutConfirmOpen}
				onClose={handleCancelLogout}
				variant="warning"
				title={LOGOUT_DIALOG.TITLE}
				primaryButtonText={LOGOUT_DIALOG.CONFIRM_LABEL}
				secondaryButtonText={LOGOUT_DIALOG.CANCEL_LABEL}
				onPrimaryClick={handleConfirmLogout}
				onSecondaryClick={handleCancelLogout}
				showCloseIcon={false}>
				<p>{LOGOUT_DIALOG.DESCRIPTION}</p>
			</DialogModal>
		</>
	);
};

const renderLandingHeader = (onNavigateHome, onDynamicRegisterOrSignInButton, isLoginPage, isMobileMenuOpen, toggleMobileMenu) => (
	<header className="header">
		<div className="container">
			<div className="header__content">
				<button
					className={clsx('header__hamburger', {
						'is-active': isMobileMenuOpen
					})}
					onClick={toggleMobileMenu}
					aria-label="Toggle menu">
					<span className="header__hamburger-line"></span>
					<span className="header__hamburger-line"></span>
					<span className="header__hamburger-line"></span>
				</button>

				<div className="header__logo">
					<ImageWithFallback
						className="app-logo"
						alt="Doping Hafıza Logo"
						src="dh-birebir-logo.svg"
						onClick={onNavigateHome}
					/>
				</div>

				<nav className="header__navigation">
					<ul className="navigation__list">
						<li className="navigation__item">
							<a
								href="/landing#"
								className="navigation__link">
								Doping Hafıza Bire Bir Nedir?
							</a>
						</li>
						<li className="navigation__item">
							<a
								href="/landing#avantajlar"
								className="navigation__link">
								Avantajları Nelerdir?
							</a>
						</li>
						<li className="navigation__item">
							<a
								href="/landing#kimler-basvurabilir"
								className="navigation__link">
								Kimler Başvurabilir?
							</a>
						</li>
						<li className="navigation__item">
							<a
								href="/landing#sikca-sorulan-sorular"
								className="navigation__link">
								Sıkça Sorulan Sorular
							</a>
						</li>
					</ul>
					<div className="navigation__auth">
						<button
							className="btn btn--primary"
							onClick={onDynamicRegisterOrSignInButton}>
							{isLoginPage ? 'Kayıt Ol' : 'Giriş Yap'}
						</button>
					</div>
				</nav>

				<div
					className={clsx('header__overlay', {
						'is-visible': isMobileMenuOpen
					})}
					onClick={toggleMobileMenu}></div>

				<div
					className={clsx('header__mobile-menu header__applicant-menu', {
						'is-open': isMobileMenuOpen
					})}>
					<div className="header__mobile-menu-header">
						<div className="header__mobile-menu-header-logo">
							<ImageWithFallback
								className="app-logo"
								alt="Doping Hafıza Logo"
								src="dh-birebir-logo.svg"
							/>
						</div>
						<button
							className="header__mobile-menu-header-close"
							onClick={toggleMobileMenu}
							aria-label="Close menu">
							×
						</button>
					</div>

					<div className="header__mobile-menu-content">
						<ul className="header__mobile-menu-list">
							<li className="header__mobile-menu-item">
								<a
									href="/landing#"
									className="header__mobile-menu-link"
									onClick={() => toggleMobileMenu()}>
									Doping Hafıza Bire Bir Nedir?
								</a>
							</li>
							<li className="header__mobile-menu-item">
								<a
									href="/landing#avantajlar"
									className="header__mobile-menu-link"
									onClick={() => toggleMobileMenu()}>
									Avantajları Nelerdir?
								</a>
							</li>
							<li className="header__mobile-menu-item">
								<a
									href="/landing#kimler-basvurabilir"
									className="header__mobile-menu-link"
									onClick={() => toggleMobileMenu()}>
									Kimler Başvurabilir?
								</a>
							</li>
							<li className="header__mobile-menu-item">
								<a
									href="/landing#sikca-sorulan-sorular"
									className="header__mobile-menu-link"
									onClick={() => toggleMobileMenu()}>
									Sıkça Sorulan Sorular
								</a>
							</li>
						</ul>
					</div>

					<div className="header__mobile-menu-footer">
						<button
							className="btn btn--primary"
							onClick={() => {
								toggleMobileMenu();
								onDynamicRegisterOrSignInButton();
							}}>
							{isLoginPage ? 'Kayıt Ol' : 'Giriş Yap'}
						</button>
					</div>
				</div>
			</div>
		</div>
	</header>
);

const renderAppHeader = (
	onNavigateHome,
	{ unreadNotificationCount, messages, onAnnouncementClick, userProfile, isApplicant, handleProfileClick, handleLogout },
	isDropdownOpen,
	toggleDropdown,
	isMobileDeviceAndInProfilePage,
	dropdownRef,
	profileRef,
	isMobileMenuOpen,
	toggleMobileMenu,
	handleMobileMenuNavigate,
	menuItems,
	openGroups,
	toggleGroup,
	onNavigateNotifications,
	isCallFeatureActive,
	handlePhoneDialerOpen,
	isPhoneDialerOpen,
	setIsPhoneDialerOpen,
	handleDirectCall,
	callModalRef,
	isReviewer,
	isActiveAssignToMe,
	hasSupportDocs,
	navigate
) => (
	<header
		className={clsx('header ', {
			'applicant-header': isApplicant
		})}>
		<div className="container">
			<div className="header__content">
				{!isApplicant && (
					<button
						className={clsx('header__hamburger', {
							'is-active': isMobileMenuOpen
						})}
						onClick={toggleMobileMenu}
						aria-label="Toggle menu">
						<span className="header__hamburger-line"></span>
						<span className="header__hamburger-line"></span>
						<span className="header__hamburger-line"></span>
					</button>
				)}
				{isApplicant && (
					<button
						className="mobile-logout"
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
							handleLogout();
						}}>
						<i className="icons-logout" />
					</button>
				)}

				<div className={clsx('header__logo', { header__applicant: isApplicant })}>
					<ImageWithFallback
						alt="DopingHafıza"
						src="dh-birebir-logo.svg"
						onClick={onNavigateHome}
					/>
				</div>

				<div className="header__controls">
					{!isApplicant && (
						<Fragment>
							<div className="controls__divider" />

							{isCallFeatureActive && (
								<>
									<button
										className="controls__button"
										onClick={handlePhoneDialerOpen}
										title="Telefon">
										<i className="icons-phone" />
									</button>
									<div className="controls__divider" />
								</>
							)}

							<button
								className="controls__button"
								onClick={onNavigateNotifications}>
								<i className="icons-bell" />
								{unreadNotificationCount > 0 && <span className="controls__badge">{unreadNotificationCount}</span>}
							</button>
							<div className="controls__divider" />

							<button
								className="controls__button"
								onClick={onAnnouncementClick}>
								<i className="icons-letter" />
								{messages > 0 && <span className="controls__badge">{messages}</span>}
							</button>

							<div className="controls__divider" />
						</Fragment>
					)}
					{userProfile && !isMobileDeviceAndInProfilePage && (
						<div
							ref={profileRef}
							className={clsx('controls__profile', {
								'is-active': isDropdownOpen
							})}
							role="button"
							tabIndex={0}
							onClick={toggleDropdown}>
							<div className="profile__avatar-wrapper">
								{userProfile.profilePicture ? (
									<div className="profile__avatar">
										<img
											src={userProfile.profilePicture}
											alt={userProfile.name}
										/>
									</div>
								) : (
									<div className="profile__avatar profile__avatar--placeholder">
										{userProfile?.name?.charAt(0)?.toUpperCase()}
										{userProfile?.surname?.charAt(0)?.toUpperCase()}
									</div>
								)}
							</div>
							<div className="profile__info">
								<span className="profile__name">
									{userProfile.name} {userProfile.surname}
								</span>
								<span className="profile__role">{userProfile.specialization}</span>
							</div>

							<div
								ref={dropdownRef}
								className={clsx('profile-dropdown', {
									'is-open': isDropdownOpen
								})}>
								<ul className="profile-dropdown__menu">
									{!isApplicant && (
										<li className="profile-dropdown__item">
											<a
												href="#"
												className="profile-dropdown__link"
												onClick={e => {
													e.preventDefault();
													e.stopPropagation();
													handleProfileClick();
												}}>
												<i className="icon icons-users" />
												Profil
												<i className="icon icons-arrow-right" />
											</a>
										</li>
									)}
									<li className="profile-dropdown__item">
										<a
											href="#"
											className="profile-dropdown__link profile-dropdown__link--logout"
											onClick={e => {
												e.preventDefault();
												e.stopPropagation();
												handleLogout();
											}}>
											<i className="icon icons-logout" />
											Çıkış Yap
											<i className="icon icons-arrow-right" />
										</a>
									</li>
								</ul>
							</div>
						</div>
					)}
				</div>

				{!isApplicant && (
					<>
						<div
							className={clsx('header__overlay', {
								'is-visible': isMobileMenuOpen
							})}
							onClick={toggleMobileMenu}></div>

						<div
							className={clsx('header__mobile-menu', {
								'is-open': isMobileMenuOpen
							})}>
							<div className="header__mobile-menu-header">
								<div className="header__mobile-menu-header-logo">
									<ImageWithFallback
										className="app-logo"
										alt="Doping Hafıza Logo"
										src="dh-birebir-light-logo.svg"
									/>
								</div>
								<button
									className="header__mobile-menu-header-close"
									onClick={toggleMobileMenu}
									aria-label="Close menu">
									×
								</button>
							</div>

							<div className="header__mobile-menu-content">
								<div
									className={clsx('header__mobile-menu-item single', {
										'is-active': NavigationPath.messages === location.pathname
									})}
									onClick={() => {
										handleMobileMenuNavigate(NavigationPath.messages);
									}}>
									<div className="header__mobile-menu-item-icon">
										<i className="icon icons-letter" />
									</div>
									<div className="header__mobile-menu-item-text">Mesajlar</div>
								</div>
								{isCallFeatureActive && (
									<div
										className="header__mobile-menu-item single"
										onClick={handlePhoneDialerOpen}>
										<div className="header__mobile-menu-item-icon">
											<i className="icons-phone" />
										</div>
										<div className="header__mobile-menu-item-text">Telefon</div>
									</div>
								)}
								{hasSupportDocs && (
									<div
										className={clsx('header__mobile-menu-item single', {
											'is-active': NavigationPath.supportDocuments === location.pathname
										})}
										onClick={() => {
											navigate(NavigationPath.supportDocuments);
											toggleMobileMenu();
										}}>
										<div className="header__mobile-menu-item-icon">
											<i className="icon icons-document" />
										</div>
										<div className="header__mobile-menu-item-text">Destek Dokümanları</div>
									</div>
								)}
								{menuItems?.map(group => {
									const serviceGroupInfo = SERVICE_GROUP_MAPPING[group.liveServiceGroupName];
									if (!serviceGroupInfo) return null;

									const rawItems = group.subMenuItems || [];
									let subMenuItems = rawItems;
									if (group.liveServiceGroupName === 'SOLVER') {
										if (isReviewer && !subMenuItems.some(i => i.name === 'EXPERT_REVIEW')) {
											subMenuItems = [{ name: 'EXPERT_REVIEW', description: 'Uzman Değerlendirme' }, ...subMenuItems];
										}
										if (isActiveAssignToMe && !subMenuItems.some(i => i.name === 'RESOLVE_QUESTIONS')) {
											const expertReviewIndex = subMenuItems.findIndex(i => i.name === 'EXPERT_REVIEW');
											const insertAt = expertReviewIndex >= 0 ? expertReviewIndex + 1 : 0;
											subMenuItems = [...subMenuItems.slice(0, insertAt), { name: 'RESOLVE_QUESTIONS', description: 'Yeniden Atanan Sorular' }, ...subMenuItems.slice(insertAt)];
										}
									}
									const isOpen = openGroups.includes(group.liveServiceGroupName);

									return (
										<div
											key={group.liveServiceGroupName}
											className="header__mobile-menu-group">
											<div
												className="header__mobile-menu-group-header"
												onClick={() => toggleGroup(group.liveServiceGroupName)}>
												<div className="header__mobile-menu-group-title">{serviceGroupInfo.icon}</div>
												<div className="header__mobile-menu-item-expander">
													<i className={isOpen ? 'sidebar__menu-arrow icons-alt-arrow-up' : 'sidebar__menu-arrow icons-alt-arrow-down'}></i>
												</div>
											</div>

											{isOpen && (
												<div className="header__mobile-menu-group-content">
													{subMenuItems.map(item => {
														const subMenuInfo = SUB_MENU_MAPPING?.[item.name];
														const isActive = subMenuInfo?.navigationPath === location.pathname;

														if (!subMenuInfo) return null;

														return (
															<div
																key={item.name}
																className={clsx('header__mobile-menu-item', {
																	'is-active': isActive
																})}
																onClick={() => {
																	handleMobileMenuNavigate(subMenuInfo.navigationPath);
																}}>
																<div className="header__mobile-menu-item-icon">
																	<i className={subMenuInfo.icon}></i>
																</div>
																<div className="header__mobile-menu-item-text">{item.description}</div>
																{item.notificationCount > 0 && (
																	<div className="header__mobile-menu-item-badge">
																		<span>{item.notificationCount}</span>
																	</div>
																)}
															</div>
														);
													})}
												</div>
											)}
										</div>
									);
								})}
							</div>

							<div className="header__mobile-menu-footer">
								<div
									className="header__mobile-menu-logout"
									onClick={handleLogout}>
									<i className="icons-logout"></i>
									<span>Çıkış Yap</span>
								</div>
							</div>
						</div>
					</>
				)}

				<PhoneDialerModal
					isOpen={isPhoneDialerOpen}
					onClose={() => setIsPhoneDialerOpen(false)}
					onCall={handleDirectCall}
				/>
				<CallModal ref={callModalRef} />
			</div>
		</div>
	</header>
);

export default HeaderView;
